// src/components/BuzzingChallenges.js
import React, { useEffect, useRef, useState } from "react";
import pb from "../lib/pb";
import { useAuth } from "../contexts/AuthContext";
import UploadChallengeModal from "./UploadChallengeModal";
import ChallengeDetailsModal from "./ChallengeDetailsModal";

/*
BuzzingChallenges component (updated)
 - clicking the card body opens ChallengeDetailsModal (fullscreen, modal-only)
 - metrics + remix flows remain as before
*/

export default function BuzzingChallenges() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openUploader, setOpenUploader] = useState(false);
  const [activeAcceptingFor, setActiveAcceptingFor] = useState(null);
  const [counters, setCounters] = useState({});
  const [detailsOpenFor, setDetailsOpenFor] = useState(null); // challenge id for modal

  // load initial
  const load = async () => {
    try {
      const list = await pb.collection("buzzing_challenges").getFullList({
        sort: "-created",
        expand: "author",
      });
      setItems(list);
    } catch (err) {
      console.error(err);
    } finally { setLoading(false); }
  };

  useEffect(() => {
    load();

    // subscribe to buzzing_challenges (realtime)
    const unsub = pb.collection("buzzing_challenges").subscribe("*", (e) => {
      setItems(prev => {
        if (e.action === "create") return [e.record, ...prev];
        if (e.action === "update") return prev.map(p => p.id === e.record.id ? e.record : p);
        if (e.action === "delete") return prev.filter(p => p.id !== e.record.id);
        return prev;
      });
    });

    // subscribe to analytics - views/accepts/votes
    const viewSub = pb.collection("challenge_views").subscribe("*", () => { aggregateCounters(); });
    const acceptSub = pb.collection("challenge_accepts").subscribe("*", () => { aggregateCounters(); });
    const voteSub = pb.collection("challenge_votes").subscribe("*", () => { aggregateCounters(); });

    // initial aggregate
    aggregateCounters();

    return () => {
      try { unsub(); viewSub(); acceptSub(); voteSub(); } catch {}
    };
  }, []);

  // helper: aggregate counters from separate collections
  const aggregateCounters = async () => {
  try {
    const views = await pb.collection("challenge_views").getFullList().catch(()=>[]);
    const votes = await pb.collection("challenge_votes").getFullList().catch(()=>[]);

    const map = {};

    for (const v of views) {
      map[v.challengeId] ??= { views:0, aye:0, no:0 };
      map[v.challengeId].views++;
    }

    for (const r of votes) {
      map[r.challengeId] ??= { views:0, aye:0, no:0 };
      if (r.vote === "aye") map[r.challengeId].aye++;
      if (r.vote === "no") map[r.challengeId].no++;
    }

    setCounters(map);
  } catch (err) {
    console.error("aggregateCounters error", err);
  }
};


  // mark view (for analytics)
  const markView = async (challengeId) => {
    if (!challengeId) return;
    try {
      await pb.collection("challenge_views").create({ challengeId, userId: user?.id || null, createdAt: new Date().toISOString() });
      aggregateCounters();
    } catch {}
  };

  // accept challenge flow -> open uploader for remix, then create acceptance doc to track it
  const onAccept = async (challenge) => {
    if (!user) return alert("Login required");
    setActiveAcceptingFor(challenge);
    setOpenUploader(true);
  };

  const onRemixPosted = async (createdRecord) => {
    try {
      if (!activeAcceptingFor) return;
      await pb.collection("challenge_accepts").create({
        challengeId: activeAcceptingFor.id,
        remixId: createdRecord.id,
        userId: user.id,
        createdAt: new Date().toISOString()
      });
      setActiveAcceptingFor(null);
      setOpenUploader(false);
      aggregateCounters();
      alert("Remix posted and acceptance saved!");
    } catch (err) {
      console.error("onRemixPosted error", err);
      alert("Failed to register acceptance");
    }
  };

  // UI card for each challenge
  const ChallengeCard = ({ item }) => {
    const c = counters[item.id] || { views:0, aye:0, no:0 };

const impact = c.aye;
const ayeRatio = c.aye ? c.views / c.aye : 0;
const noRatio = c.no ? c.views / c.no : 0;

const isPositive = ayeRatio >= noRatio;
const delta = Math.abs(ayeRatio - noRatio).toFixed(1);
    const mediaUrl = item.media ? pb.files.getURL(item, item.media) : null;

    return (
      <div
        className="buzz-card"
        style={{
          width: 180, minWidth: 180, marginRight: 12, background: "#0c0c0d", borderRadius: 10, padding: 8, color: "#fff", cursor: "pointer"
        }}
        // clicking body opens fullscreen modal (stay modal)
        onClick={() => {
          setDetailsOpenFor(item.id);
          // also mark view immediately
          markView(item.id);
        }}
      >
        <div style={{ height: 220, borderRadius: 8, overflow: "hidden", background: "#000" }}>
          {item.mediaType === "Video" && mediaUrl ? (
            <video src={mediaUrl} playsInline muted style={{ width: "100%", height: "100%", objectFit: "cover" }} />
          ) : item.mediaType === "Image" && item.media ? (
            <img src={mediaUrl} alt={item.title} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
          ) : <div style={{ padding: 20, color: "#999" }}>No media</div>}
        </div>

        <div style={{ marginTop: 8, display:"flex", justifyContent:"space-between", gap:8 }}>
  <div>
    <strong style={{ fontSize: 14 }}>
      {item.title || (item.content || "Untitled")}
    </strong>
    <div style={{ fontSize: 12, color: "#ccc", marginTop: 6 }}>
      {item.expand?.author?.username || "Anon"}
    </div>
  </div>

  <div style={{ textAlign:"right", fontSize:12 }}>
    <div style={{ fontWeight:600 }}>Impact {impact}</div>
    <div style={{ color: isPositive ? "#2ecc71" : "#ff4d4f" }}>
      {isPositive ? "↑" : "↓"} {delta}
    </div>
  </div>
</div>


        <div style={{ display: "flex", gap: 8, marginTop: 8, alignItems: "center", justifyContent: "space-between" }}>
          <div style={{ fontSize: 12, color: "#9aa" }}>
            <div>👁 {c.views}</div>
            <div>✅ {c.accepts}</div>
            <div>🗳 {c.votes}</div>
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {/* Keep existing Open button but it will open modal too (we preserve it for backward compatibility) */}
            <button onClick={(e) => { e.stopPropagation(); markView(item.id); setDetailsOpenFor(item.id); }} style={{ padding: "6px 8px", borderRadius: 6 }}>Open</button>
            <button onClick={(e) => { e.stopPropagation(); onAccept(item); }} style={{ padding: "6px 8px", borderRadius: 6, background: "#ff6ec4", color: "#fff" }}>Accept</button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div style={{ width: "100%" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
        <h4 style={{ margin: 0 }}>Buzzing</h4>
        <div>
          <button onClick={() => setOpenUploader(true)} style={{ padding: "8px 10px", borderRadius: 8, border: "1px solid #333" }}>+ Create</button>
        </div>
      </div>

      <div style={{ display: "flex", overflowX: "auto", paddingBottom: 8 }}>
        {/* Create Card */}
        <div style={{ width: 180, minWidth: 180, marginRight: 12, background: "linear-gradient(180deg,#071018,#0b0b0f)", borderRadius: 10, padding: 12, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <button onClick={() => setOpenUploader(true)} style={{ padding: "12px 18px", borderRadius: 8, border: "none", background: "#00c48c", color: "#071012", cursor: "pointer" }}>Create Buzz</button>
        </div>

        {/* Items */}
        {loading ? <div style={{ color: "#aaa" }}>Loading…</div> : items.map(it => <ChallengeCard key={it.id} item={it} />)}
      </div>

      {/* Uploader modal */}
      {openUploader && (
        <UploadChallengeModal
          open={openUploader}
          onClose={() => { setOpenUploader(false); setActiveAcceptingFor(null); }}
          onPosted={(rec) => {
            if (activeAcceptingFor) {
              (async () => {
                try {
                  await pb.collection("challenge_accepts").create({
                    challengeId: activeAcceptingFor.id,
                    remixId: rec.id,
                    userId: user?.id || null,
                    createdAt: new Date().toISOString()
                  });
                  setActiveAcceptingFor(null);
                  setOpenUploader(false);
                  alert("Remix posted and acceptance registered.");
                  aggregateCounters();
                } catch (err) {
                  console.error(err);
                }
              })();
            } else {
              setOpenUploader(false);
              aggregateCounters();
            }
          }}
        />
      )}

      {/* Details modal - stays fullscreen (Use B) */}
      {detailsOpenFor && (
        <ChallengeDetailsModal
          open={!!detailsOpenFor}
          challengeId={detailsOpenFor}
          onClose={() => setDetailsOpenFor(null)}
        />
      )}
    </div>
  );
}